import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils/cn';

const toggleVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-base font-medium transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:enabled:bg-primary/80 data-[state=on]:bg-primary/50',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:enabled:bg-destructive/80 data-[state=on]:bg-destructive/50',
        outline:
          'border-input bg-transparent shadow-xs hover:enabled:bg-accent/80 hover:enabled:text-accent-foreground data-[state=on]:bg-accent/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:enabled:bg-secondary/70 data-[state=on]:bg-secondary/50',
        accent:
          'bg-accent text-accent-foreground shadow-xs hover:enabled:bg-accent/80 data-[state=on]:bg-accent/50',
        ghost:
          'bg-transparent hover:enabled:bg-accent/80 hover:enabled:text-accent-foreground data-[state=on]:bg-accent/50',
        link: 'text-accent decoration-accent/80 underline-offset-4 hover:enabled:text-accent/80 hover:enabled:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-8 px-3 text-xs',
        sm: 'h-8 px-3 text-sm',
        lg: 'h-10 px-8',
        icon: 'size-9 rounded-full',
        'icon-sm': 'size-8 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
